import { useEffect, useMemo, useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Combobox, NoBorderButtonWrap } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { ChipTrigger } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/brands-filter/BrandsFilter.styled";
import { MultiselectFilterOption } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/MultiselectFilterOption";

const CORE_COMPETITORS_ITEM_ID = "_CoreCompetitors";

interface IBrandOption {
    id: string;
    value: string;
    label: string;
}

interface IBrandsFilterProps {
    options: IBrandOption[];
    selectedBrands: string[];
    coreCompetitorBrands?: string[];
    isCoreCompetitorsSelected: boolean;
    onSelectionChange: (brands: string[], isCoreCompetitorsSelected: boolean) => void;
    resolveLabel: (value: string[] | undefined) => string;
    onOpen?: () => void;
    onApply?: () => void;
    isDisabled?: boolean;
}

const buildAppliedKeys = (brands: string[], coreSelected: boolean): Set<string> => {
    const keys = new Set<string>(brands);
    if (coreSelected) {
        keys.add(CORE_COMPETITORS_ITEM_ID);
    }

    return keys;
};

export const BrandsFilter = ({
    options,
    selectedBrands,
    coreCompetitorBrands,
    isCoreCompetitorsSelected,
    onSelectionChange,
    resolveLabel,
    onOpen,
    onApply,
    isDisabled,
}: IBrandsFilterProps) => {
    const [isOpen, setOpen] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [tempKeys, setTempKeys] = useState<Set<string>>(buildAppliedKeys(selectedBrands, isCoreCompetitorsSelected));
    const showCoreCompetitorsOption = (coreCompetitorBrands?.length ?? 0) > 0;
    const total = selectedBrands.length + (isCoreCompetitorsSelected ? 1 : 0);
    const coreCompetitorsLabel = translate("llm.prompts.filters.brands.core_competitors");
    const appliedValue = isCoreCompetitorsSelected ? [coreCompetitorsLabel, ...selectedBrands] : selectedBrands;

    useEffect(() => {
        if (!isOpen) {
            const keys = buildAppliedKeys(selectedBrands, isCoreCompetitorsSelected);
            setTempKeys(keys);
        }
    }, [isOpen, selectedBrands, isCoreCompetitorsSelected]);

    const filteredOptions = useMemo(() => {
        const lowercaseTerm = searchTerm.toLowerCase();
        return searchTerm ? options.filter((opt) => opt.label?.toLowerCase().includes(lowercaseTerm)) : options;
    }, [options, searchTerm]);

    const toggleSelectedOption = (id: string): void => {
        setTempKeys((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }

            return next;
        });
    };

    const handleOpenChange = (open: boolean): void => {
        if (isDisabled) {
            return;
        }

        if (open) {
            setTempKeys(buildAppliedKeys(selectedBrands, isCoreCompetitorsSelected));
            setSearchTerm("");
            onOpen?.();
        }

        setOpen(open);
    };

    const apply = (): void => {
        const nextCoreCompetitorsSelected = tempKeys.has(CORE_COMPETITORS_ITEM_ID);
        const nextBrands = [...tempKeys].filter((key) => key !== CORE_COMPETITORS_ITEM_ID);
        const sameLength = nextBrands.length === selectedBrands.length;
        const sameBrands = sameLength && nextBrands.every((brand) => selectedBrands.includes(brand));
        const coreCompetitorsChanged = nextCoreCompetitorsSelected !== isCoreCompetitorsSelected;

        if (coreCompetitorsChanged || !sameBrands) {
            onSelectionChange(nextBrands, nextCoreCompetitorsSelected);
            onApply?.();
        }

        setOpen(false);
    };

    const clear = (): void => {
        onSelectionChange([], false);
        setSearchTerm("");
    };

    return (
        <Combobox
            isOpen={isOpen}
            onOpenChange={handleOpenChange}
            placement="bottom-start"
            width="250px"
            data-automation="prompts-brands-filter"
        >
            <Combobox.Trigger>
                <div>
                    {total ? (
                        <ChipTrigger
                            isOpen={isOpen}
                            text={resolveLabel(appliedValue)}
                            onCloseItem={isDisabled ? undefined : clear}
                            isDisabled={isDisabled}
                            data-automation="prompts-brands-filter-chip"
                        />
                    ) : (
                        <NoBorderButtonWrap
                            disabled={isDisabled}
                            data-automation="prompts-brands-filter-button"
                        >
                            {resolveLabel(undefined)}
                        </NoBorderButtonWrap>
                    )}
                </div>
            </Combobox.Trigger>
            <Combobox.SearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder={translate("llm.prompts.filters.brands.search_placeholder")}
            />
            <Combobox.Content emptyState={translate("filter_factory.search.no_items")}>
                {showCoreCompetitorsOption && (
                    <Combobox.Option
                        value={{ id: CORE_COMPETITORS_ITEM_ID, label: coreCompetitorsLabel }}
                        textValue={coreCompetitorsLabel}
                        onClick={() => toggleSelectedOption(CORE_COMPETITORS_ITEM_ID)}
                    >
                        {filteredOptions.length > 0 ? (
                            <Flex
                                alignItems="center"
                                justifyContent="space-between"
                                width="100%"
                                padding="0 0 8px 0"
                                borderBottom={`1px solid ${colorsPalettes.carbon[100]}`}
                            >
                                <MultiselectFilterOption label={coreCompetitorsLabel} />
                                <Combobox.OptionCheckbox isChecked={tempKeys.has(CORE_COMPETITORS_ITEM_ID)} />
                            </Flex>
                        ) : (
                            <>
                                <MultiselectFilterOption label={coreCompetitorsLabel} />
                                <Combobox.OptionCheckbox isChecked={tempKeys.has(CORE_COMPETITORS_ITEM_ID)} />
                            </>
                        )}
                    </Combobox.Option>
                )}
                {filteredOptions.map((item) => (
                    <Combobox.Option
                        key={item.id}
                        value={item}
                        textValue={item.label}
                        onClick={() => toggleSelectedOption(item.id)}
                    >
                        <MultiselectFilterOption label={item.label} />
                        <Combobox.OptionCheckbox isChecked={tempKeys.has(item.id)} />
                    </Combobox.Option>
                ))}
            </Combobox.Content>
            <Combobox.ApplyButton onClick={apply}>{translate("filter_factory.button.apply")}</Combobox.ApplyButton>
        </Combobox>
    );
};
